import dayjs from 'dayjs/esm';

import type { SessionBase } from '@asi/auth/types-auth';
import type { BaseStorageService } from '@cosmos/data-access-storage';

/**
 * Represents the payload structure stored in local storage for customer portal auth
 */
export interface CustomerPortalCollectionStorageAuthDataItem {
  session: SessionBase;
  accessCode: string;
  expires: string;
}

export type CustomerPortalCollectionAuthStoragePayload = Record<
  string,
  CustomerPortalCollectionStorageAuthDataItem | null
>;

const CUSTOMER_PORTAL_COLLECTION_AUTH_STORAGE_KEY =
  'CUSTOMER_PORTAL_COLLECTION_AUTH_STORAGE_KEY';

export function getCustomerPortalCollectionAuthDataFromLocalStorage(
  ls: BaseStorageService,
  collectionId: string
) {
  const authData = ls.getItem<CustomerPortalCollectionAuthStoragePayload>(
    CUSTOMER_PORTAL_COLLECTION_AUTH_STORAGE_KEY
  )?.[collectionId];

  if (!authData) {
    return null;
  }

  if (
    typeof authData.accessCode !== 'string' ||
    typeof authData.session !== 'object'
  ) {
    removeAuthDataForCollectionId(ls, collectionId);
    return null;
  }

  const expires = new Date(authData.expires).getTime();
  if (isNaN(expires) || new Date().getTime() > expires) {
    removeAuthDataForCollectionId(ls, collectionId);
    return null;
  }

  return authData;
}

export function setCustomPortalCollectionAuthDataToLocalStorage(
  ls: BaseStorageService,
  collectionId: string,
  payload: Partial<
    Pick<CustomerPortalCollectionStorageAuthDataItem, 'accessCode' | 'session'>
  >
) {
  let authData = ls.getItem<CustomerPortalCollectionAuthStoragePayload>(
    CUSTOMER_PORTAL_COLLECTION_AUTH_STORAGE_KEY
  );
  if (typeof authData !== 'object') {
    authData = {};
  }

  let currentCollectionAuthData: CustomerPortalCollectionStorageAuthDataItem | null =
    null;
  const existingExpiration = authData?.[collectionId!]?.expires;
  if (payload.accessCode && payload.session && collectionId) {
    currentCollectionAuthData = {
      session: payload.session,
      accessCode: payload.accessCode,
      expires: existingExpiration ?? dayjs().add(30, 'days').toISOString(),
    };
  }

  ls.setItem(CUSTOMER_PORTAL_COLLECTION_AUTH_STORAGE_KEY, {
    ...authData,
    [collectionId]: currentCollectionAuthData,
  });
}

export function removeAuthDataForCollectionId(
  ls: BaseStorageService,
  collectionId: string
) {
  ls.setItem(CUSTOMER_PORTAL_COLLECTION_AUTH_STORAGE_KEY, {
    ...ls.getItem<CustomerPortalCollectionAuthStoragePayload>(
      CUSTOMER_PORTAL_COLLECTION_AUTH_STORAGE_KEY
    ),
    [collectionId]: null,
  });
}

const CUSTOMER_PORTAL_PRODUCT_AUTH_STORAGE_KEY =
  'CUSTOMER_PORTAL_PRODUCT_AUTH_STORAGE_KEY';

export function getCustomerPortalProductAuthDataFromLocalStorage(
  ls: BaseStorageService,
  productId: string
) {
  const authData = ls.getItem<CustomerPortalCollectionAuthStoragePayload>(
    CUSTOMER_PORTAL_PRODUCT_AUTH_STORAGE_KEY
  )?.[productId];

  if (!authData) {
    return null;
  }

  if (
    typeof authData.accessCode !== 'string' ||
    typeof authData.session !== 'object'
  ) {
    removeAuthDataForProductId(ls, productId);
    return null;
  }

  const expires = new Date(authData.expires).getTime();
  if (isNaN(expires) || new Date().getTime() > expires) {
    removeAuthDataForProductId(ls, productId);
    return null;
  }

  return authData;
}

export function setCustomPortalProductAuthDataToLocalStorage(
  ls: BaseStorageService,
  productId: string,
  payload: Partial<
    Pick<CustomerPortalCollectionStorageAuthDataItem, 'accessCode' | 'session'>
  >
) {
  let authData = ls.getItem<CustomerPortalCollectionAuthStoragePayload>(
    CUSTOMER_PORTAL_PRODUCT_AUTH_STORAGE_KEY
  );
  if (typeof authData !== 'object') {
    authData = {};
  }

  let currentProductAuthData: CustomerPortalCollectionStorageAuthDataItem | null =
    null;
  const existingExpiration = authData?.[productId]?.expires;
  if (payload.accessCode && payload.session && productId) {
    currentProductAuthData = {
      session: payload.session,
      accessCode: payload.accessCode,
      expires: existingExpiration ?? dayjs().add(30, 'days').toISOString(),
    };
  }

  ls.setItem(CUSTOMER_PORTAL_PRODUCT_AUTH_STORAGE_KEY, {
    ...authData,
    [productId]: currentProductAuthData,
  });
}

export function removeAuthDataForProductId(
  ls: BaseStorageService,
  productId: string
) {
  ls.setItem(CUSTOMER_PORTAL_PRODUCT_AUTH_STORAGE_KEY, {
    ...ls.getItem<CustomerPortalCollectionAuthStoragePayload>(
      CUSTOMER_PORTAL_PRODUCT_AUTH_STORAGE_KEY
    ),
    [productId]: null,
  });
}
